/**
 * Resilver Risk Assessment Result Component
 *
 * Displays:
 * - Resilver time (hours/days)
 * - Risk window duration
 * - URE probability
 * - Multi-drive failure probability
 * - Combined data loss probability
 * - Risk level indicator
 */

import { Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableRow, Alert, Chip } from '@mui/material';
import type { ReliabilityResults } from '../../types';
import { TooltipIcon } from '../ui';

interface ResilverRiskProps {
  results: ReliabilityResults;
}

export function ResilverRisk({ results }: ResilverRiskProps) {
  const formatPercent = (value: number) => {
    if (value < 0.0001) return `${(value * 10000).toFixed(2)}%`;
    if (value < 0.01) return `${(value * 100).toFixed(3)}%`;
    return `${(value * 100).toFixed(2)}%`;
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Resilver Risk Assessment
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Alert
          severity={results.riskLevel === 'low' ? 'success' : results.riskLevel === 'medium' ? 'warning' : results.riskLevel === 'high' ? 'warning' : 'error'}
          sx={{ mb: 2 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1">
              Risk Level: <strong>{results.riskLevel.toUpperCase()}</strong> <TooltipIcon tooltipKey="riskLevel" />
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Chip
                label={`Max Tolerable Failures: ${results.maxTolerableFailures}`}
                size="small"
                color={results.maxTolerableFailures >= 2 ? 'success' : 'default'}
              />
              <TooltipIcon tooltipKey="maxTolerableFailures" />
            </Box>
          </Box>
        </Alert>

        <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Estimated Resilver Time <TooltipIcon tooltipKey="resilverTime" />
          </Typography>
          <Typography variant="h4">
            {results.resilverTimeHours.toFixed(1)} hours
            <Typography component="span" variant="h6" sx={{ ml: 1, color: 'text.secondary' }}>
              ({results.resilverTimeDays.toFixed(1)} days)
            </Typography>
          </Typography>
        </Box>
      </Box>

      <Typography variant="subtitle1" gutterBottom>
        Failure Probability Analysis
      </Typography>

      <TableContainer>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell>URE During Resilver <TooltipIcon tooltipKey="URE" /></TableCell>
              <TableCell align="right">
                <Typography variant="body2" sx={{ color: formatPercent(results.ureProbability).includes('10') ? 'error.main' : 'inherit' }}>
                  {formatPercent(results.ureProbability)}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Multi-Drive Failure During Resilver <TooltipIcon tooltipKey="multiDriveFailure" /></TableCell>
              <TableCell align="right">
                {formatPercent(results.multiDriveFailureProbability)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Combined Data Loss Probability</strong> <TooltipIcon tooltipKey="combinedDataLossProbability" /></TableCell>
              <TableCell align="right">
                <Typography
                  variant="body2"
                  sx={{
                    color: results.totalDataLossProbability > 0.05 ? 'error.main' :
                           results.totalDataLossProbability > 0.01 ? 'warning.main' : 'success.main',
                    fontWeight: 'bold'
                  }}
                >
                  {formatPercent(results.totalDataLossProbability)}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>VDEV Failure Probability (Annual) <TooltipIcon tooltipKey="vdevFailureProbability" /></TableCell>
              <TableCell align="right">
                {formatPercent(results.vdevFailureProbability)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Pool Failure Probability (Annual) <TooltipIcon tooltipKey="poolFailureProbability" /></TableCell>
              <TableCell align="right">
                {formatPercent(results.poolFailureProbability)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row" sx={{ borderBottom: results.mttpYearsAdvanced !== undefined ? undefined : 'none' }}>
                <strong>Mean Time to Pool Loss (MTTP) <TooltipIcon tooltipKey="MTTP" /></strong>
              </TableCell>
              <TableCell align="right" sx={{ borderBottom: results.mttpYearsAdvanced !== undefined ? undefined : 'none' }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {results.mttpYears < 1
                    ? `${results.mttpMonths.toFixed(1)} months`
                    : results.mttpYears < 100
                    ? `${results.mttpYears.toFixed(1)} years`
                    : '>100 years'}
                </Typography>
                {results.mttpYearsAdvanced !== undefined && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    (Simple exponential model)
                  </Typography>
                )}
              </TableCell>
            </TableRow>
            {results.mttpYearsAdvanced !== undefined && (
              <TableRow>
                <TableCell component="th" scope="row" sx={{ borderBottom: 'none' }}>
                  <strong>Advanced MTTP (Weibull) <TooltipIcon tooltipKey="useWeibullDistribution" /></strong>
                </TableCell>
                <TableCell align="right" sx={{ borderBottom: 'none' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                    {results.mttpYearsAdvanced < 1
                      ? `${results.mttpMonthsAdvanced!.toFixed(1)} months`
                      : results.mttpYearsAdvanced < 100
                      ? `${results.mttpYearsAdvanced!.toFixed(1)} years`
                      : '>100 years'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {(() => {
                      const diff = ((results.mttpYearsAdvanced! - results.mttpYears) / results.mttpYears) * 100;
                      const absDiff = Math.abs(diff).toFixed(0);
                      if (diff < -1) {
                        return `${absDiff}% lower (accounts for wear-out)`;
                      } else if (diff > 1) {
                        return `${absDiff}% higher (accounts for infant mortality)`;
                      } else {
                        return '~Same as simple model';
                      }
                    })()}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          * URE = Unrecoverable Read Error. Risk increases with drive size and fill level.
        </Typography>
      </Box>
    </Paper>
  );
}
